import React, {useCallback, useEffect, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import axios from "axios";
import Utils, {createUUId, deepCopy} from "../../../../../../../core/services/utils/utils";
import {Autocomplete, Box, Chip, CircularProgress, IconButton, TextField, Typography} from "@mui/material";
import {KeyboardReturnRounded, OpenInNewOutlined} from "@mui/icons-material";
import Api from "../../../../../../../core/services/api/api";
import {usePartnerInformation} from "../../../../../../hooks/use-partner-information";
import Regexes from "../../../../../../../core/models/regexes";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {useHistory} from "react-router-dom";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {useTranslation} from "react-i18next";


const LookupSearchViewSearchAutocomplete = ({
                                                searchData,
                                                setSearchData,
                                                loading,
                                                disabled,
                                                searchSchema,
                                                searchToken,
                                                startAdornment,
                                                endAdornment,
                                                searchIcon,
                                                sx,
                                                InputProps,
                                                TextFieldProps,
                                                AutocompleteProps,
                                                isAdvancedMode,
                                                hideSuggestions,
                                                showPartQuickLinks,
                                            }) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [keywords, setKeywords] = useState('');
    const [value_mock, setValue_mock] = useState('');
    const [getOptions, setGetOptions] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [error, setError] = useState(null);
    const isMounted = useIsMounted();
    const keywordsRef = React.useRef(keywords);
    keywordsRef.current = keywords;
    /** When true, the next input reset (after keyword submit) must keep part quick-link chips. */
    const preservePartQuickLinksOnResetRef = useRef(false);
    const prevIndustryIdRef = useRef(searchData.industryId);
    const partnerInformation = usePartnerInformation();
    const operatorAccessLevels = useAccessLevels();
    const history = useHistory();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.lookup-v2-search-autocomplete'});
    const [partQuickLinks, setPartQuickLinks] = useState([]);

    /**
     * Clears the error after 5 seconds.
     */
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    /**
     * Clears the error when the keywords change.
     */
    useEffect(() => {
        const trimmed = keywords?.trim() ?? "";
        if (!trimmed?.length) {
            setError(null);
        }
    }, [keywords]);

    /**
     * Clears part quick links when the feature is off or the industry changes.
     */
    useEffect(() => {
        if (!showPartQuickLinks) {
            setPartQuickLinks([]);
            return;
        }
        if (prevIndustryIdRef.current !== searchData.industryId) {
            prevIndustryIdRef.current = searchData.industryId;
            setPartQuickLinks([]);
        }
    }, [showPartQuickLinks, searchData.industryId]);

    /**
     * Highlights the mismatches in the suggestions.
     */
    const highlightMismatches = useCallback(({data, keywords, highlightPreTag, highlightPostTag}) => {
        return data.map(item => {
            const {display} = item;

            // Regex to capture the <strong>...</strong> part
            const strongTagRegex = /<strong>(.*?)<\/strong>/g;
            let updatedDisplayValue = display;

            // Search for all <strong></strong> tags in the displayValue
            let match;
            while ((match = strongTagRegex.exec(display)) !== null) {
                const strongText = match[1];  // Text inside <strong></strong>
                let forwardMismatches = new Array(strongText.length).fill(false);
                let backwardMismatches = new Array(strongText.length).fill(false);

                // Forward pass: Compare character by character from start to end
                for (let i = 0; i < strongText.length; i++) {
                    if (i >= keywords.length || strongText[i].toLowerCase() !== keywords[i].toLowerCase()) {
                        forwardMismatches[i] = true;
                    }
                }

                // Backward pass: Compare character by character from end to start
                for (let i = strongText.length - 1, j = keywords.length - 1; i >= 0; i--, j--) {
                    if (j < 0 || strongText[i].toLowerCase() !== keywords[j].toLowerCase()) {
                        backwardMismatches[i] = true;
                    }
                }

                // Combine the results of forward and backward passes (OR logic)
                let highlightedText = '';
                for (let i = 0; i < strongText.length; i++) {
                    if (forwardMismatches[i] && backwardMismatches[i]) {
                        highlightedText += highlightPreTag + strongText[i] + highlightPostTag;
                    } else {
                        highlightedText += strongText[i];  // No mismatch, keep as is
                    }
                }

                // Replace the original <strong>...</strong> part with the highlighted version
                updatedDisplayValue = updatedDisplayValue.replace(
                    `<strong>${strongText}</strong>`,
                    `<strong>${highlightedText}</strong>`
                );
            }

            // Return the updated item with highlighted mismatches and the rest of the display
            return {
                ...item,
                display: updatedDisplayValue
            };
        });
    }, []);

    /**loadingSearchData
     * Fetches the partners and their warehouses
     * @type {(function(*): void)}
     */
    const getKeywordSuggestions = useCallback(async (cancelToken) => {
        if ((keywords?.length ?? 0) < 2 || hideSuggestions) {
            setOptions([]);
            return;
        }
        setLoadingOptions(true);

        const forApi = {
            isAdvancedMode: isAdvancedMode,
            industryId: searchData.industryId,
            keyword: keywords,
            searchToken: searchToken,
            nexusSuggestionTag: {
                highlightPreTag: "<strong>",
                highlightPostTag: "</strong>",
            }
        }
        const response = await Api.getNexusSearchSuggestion(forApi, cancelToken);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const suggestionOptions = response?.data.map(e => {
                const schemaEntry = searchSchema.entries.find(s => s.name === e.name);
                return ({
                    key: createUUId(),
                    value: e.value,
                    display: e.displayValue,
                    name: e.name,
                    typeTitle: schemaEntry?.title ?? "Keywords",
                    filterId: schemaEntry?.id ?? null,
                })
            }) ?? [];
            const uniqueOptions = {};
            for (const option of suggestionOptions) {
                uniqueOptions[`${option.name}___${option.value?.toString()}`] = option;
            }
            setOptions(highlightMismatches({
                data: Object.values(uniqueOptions),
                keywords: keywords,
                highlightPreTag: "<span>",
                highlightPostTag: "</span>",
            }));
        }
        if (!response?.aborted) {
            setLoadingOptions(false);
        }
    }, [
        hideSuggestions,
        highlightMismatches,
        isMounted,
        keywords,
        searchData.industryId,
        searchSchema.entries,
        searchToken,
        isAdvancedMode,
    ])


    /**
     * When the keyword changes, fetch the suggestions.
     */
    useEffect(() => {
        if (!getOptions)
            return
        const cancelTokenSource = axios.CancelToken.source();
        const timer = setTimeout(() => void getKeywordSuggestions(cancelTokenSource.token), 300);
        return () => {
            clearTimeout(timer);
            cancelTokenSource.cancel()
        };
    }, [getOptions, getKeywordSuggestions]);

    /**
     * Fetches catalog part numbers for quick-link chips on the products list.
     * Short/empty keywords do not clear chips — that is handled only on user typing.
     */
    const getPartQuickLinks = useCallback(async (cancelToken) => {
        if (!showPartQuickLinks || (keywords?.trim()?.length ?? 0) < 2) {
            return;
        }
        const response = await Api.autoCompletePartLookup(
            keywords.trim(),
            searchData.industryId,
            cancelToken,
        );
        if (!isMounted() || cancelToken?.reason) {
            return;
        }
        if (response?.resultFlag) {
            setPartQuickLinks((response.data ?? []).slice(0, 8));
            return;
        }
        setPartQuickLinks([]);
    }, [showPartQuickLinks, keywords, searchData.industryId, isMounted]);

    /**
     * When the keyword is long enough, fetch part quick links (independent of Nexus suggestions).
     */
    useEffect(() => {
        if (!showPartQuickLinks || (keywords?.trim()?.length ?? 0) < 2) {
            return;
        }
        const cancelTokenSource = axios.CancelToken.source();
        const timer = setTimeout(() => void getPartQuickLinks(cancelTokenSource.token), 300);
        return () => {
            clearTimeout(timer);
            cancelTokenSource.cancel();
        };
    }, [showPartQuickLinks, getPartQuickLinks, keywords]);

    /**
     * Navigates to the product editor for a catalog part chip.
     */
    const onPartQuickLinkClicked = useCallback((part) => {
        if (!part?.id || !operatorAccessLevels.packages.lookup.productInformation) {
            return;
        }
        history.push(routeFunctions.products.single(part.id));
    }, [history, operatorAccessLevels.packages.lookup.productInformation]);

    /**
     * Checks if the keywords contain any errors and returns the error message if they do.
     * @type {function(*): [string|null,*|string]}
     */
    const keywordsError = useCallback((keywords) => {
        /**
         * Checks if the string contains special characters
         * @type {function(string=): boolean}
         */
        const containsSpecialCharacters = (str) => {
            str = str?.trim() ?? "";
            return str.match(Regexes.noSpecialCharacters) !== null;
        };

        const errors = [];
        let fixedKeywords = keywords?.trim() ?? "";

        const skipSpecialCharacterError = Utils.skipSpecialCharacterError(partnerInformation?.id);
        if (!skipSpecialCharacterError && containsSpecialCharacters(keywords)) {
            errors.push("You do not need to include special characters in your search. We already take care of that.");
            fixedKeywords = fixedKeywords.replaceAll(Regexes.specialCharacters, '');
        }

        const errorString = errors.join(' ').trim();
        return [!!errorString ? errorString : null, fixedKeywords];
    }, [partnerInformation?.id])


    /**
     * Sets the value of the autocomplete.
     * @param event
     * @param _value
     * @param reason
     * @returns {Promise<*>}
     */
    const setValue = useCallback(async (event, _value, reason) => {
        setLoadingOptions(false);
        let value = deepCopy(_value);
        if (!!event && reason === 'selectOption' && !event.target.closest('.MuiAutocomplete-option') && !!keywordsRef.current?.length) {
            value = keywordsRef.current;
        }
        if (!value)
            return;

        const submittingKeywords = typeof value === 'string';

        // if force set the keywords.
        if (!isAdvancedMode) {
            if (submittingKeywords) {
                const trimmed = value?.trim() ?? "";
                const [errors] = keywordsError(trimmed);
                if (!errors) {
                    setSearchData(prevState => ({...prevState, keyword: value}));
                }
                return errors;
            } else {
                setSearchData(prevState => ({...prevState, keyword: value.value}));
            }
            return;
        }

        // if keywords are submitted,
        if (submittingKeywords) {
            const trimmed = value?.trim() ?? "";
            const foundOption = options.find(e => e.value.toLowerCase() === value.toLowerCase());
            if (!!foundOption) {
                value = foundOption;
            } else {
                const [errors] = keywordsError(trimmed);
                if (!errors) {
                    setSearchData(prevState => ({...prevState, keyword: value}));
                }
                return errors;
            }
        }

        if (value.filterId === null) {
            setSearchData(prevState => ({...prevState, keyword: value.value}));
            return;
        }

        setSearchData(prevState => {
            const foundFilter = prevState.filters.find(e => e.filterId === value.filterId);
            if (foundFilter) {
                return prevState;
            }
            if (!value.name) {
                return {
                    ...prevState,
                    keyword: value.value,
                }
            }
            return {
                ...prevState,
                filters: [...prevState.filters, {filterId: value.filterId, value: value.value}]
            }
        })
    }, [keywordsError, isAdvancedMode, options, setSearchData])

    /**
     * When the value changes, set the value_mock
     * @type {(function(*, *): void)}
     */
    const onValueChanged = useCallback(async (event, _value, reason) => {
        const submittingKeywords = typeof _value === 'string'
            || (!!event && reason === 'selectOption' && !event?.target?.closest?.('.MuiAutocomplete-option') && !!keywordsRef.current?.length);
        if (showPartQuickLinks && submittingKeywords) {
            preservePartQuickLinksOnResetRef.current = true;
        }
        const error = await setValue(event, _value, reason);
        if (!!error) {
            setError(error);
            preservePartQuickLinksOnResetRef.current = false;
        } else {
            setValue_mock(prevState => prevState === '' ? ' ' : '');
        }
    }, [setValue, showPartQuickLinks])

    /**
     * When the search button is clicked, search for the keywords.
     * @type {(function(): void)}
     */
    const onSearchClicked = useCallback(() => {
        if (!keywords?.trim())
            return;
        if (showPartQuickLinks) {
            preservePartQuickLinksOnResetRef.current = true;
        }
        onValueChanged(null, keywords);
    }, [keywords, onValueChanged, showPartQuickLinks])

    /**
     * Updates local keywords from Autocomplete input changes.
     * Typing clears/refetches quick links; submit-driven resets keep them.
     */
    const onKeywordsInputChange = useCallback((event, value, reason) => {
        const [errors, correctedValue] = keywordsError(value);
        const nextKeywords = errors ? correctedValue : value;
        if (errors) {
            setError(errors);
        }

        const isSubmitDrivenReset = reason === 'reset' || preservePartQuickLinksOnResetRef.current;
        if (preservePartQuickLinksOnResetRef.current && (reason === 'reset' || !(nextKeywords?.trim()?.length))) {
            preservePartQuickLinksOnResetRef.current = false;
        }

        setKeywords(nextKeywords);

        if (showPartQuickLinks && reason === 'input' && !isSubmitDrivenReset && (nextKeywords?.trim()?.length ?? 0) < 2) {
            setPartQuickLinks([]);
        }
    }, [keywordsError, showPartQuickLinks])


    return (
        <>
            <Box sx={sx}>
                <Autocomplete
                    fullWidth
                    freeSolo
                    disabled={loading || disabled}
                    disableClearable
                    onFocus={() => setGetOptions(true)}
                    options={options}
                    value={value_mock}
                    {...(hideSuggestions ? {open: false, onOpen: () => void 0, onClose: () => void 0} : {})}
                    {...(!hideSuggestions ? {open: open, onOpen: () => setOpen(true), onClose: () => setOpen(false)} : {})}
                    filterOptions={(options) => {
                        const selectedOptionKeys = searchData.filters.map(e => e.filterId);
                        return options.filter(e => !selectedOptionKeys.includes(e.filterId));
                    }}
                    inputValue={keywords}
                    onInputChange={onKeywordsInputChange}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value && option?.name === value?.name}
                    onChange={onValueChanged}
                    size={'small'}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option?.title ?? ''}
                    getOptionKey={(option) => option?.key ?? ""}
                    renderOption={(props, option) => {
                        return (
                            <Box component={'span'} {...props} key={props.key}>
                                <Typography
                                    variant={'body2'}
                                    sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, width: '100%'}}
                                >
                                    <Typography
                                        component={'span'}
                                        variant={'body2'}
                                        sx={theme => ({
                                            'strong': {color: 'success.main', fontSize: theme.typography.body1.fontSize},
                                            'span': {color: 'error.main'},
                                        })}
                                        dangerouslySetInnerHTML={{__html: option?.display ?? ""}}
                                    />
                                    <Typography
                                        component={'span'}
                                        variant={'caption'}
                                        fontWeight={600}
                                        color={'text.disabled'}
                                    >
                                        ({option.typeTitle ?? ""})
                                    </Typography>
                                </Typography>
                            </Box>
                        );
                    }}
                    componentsProps={{
                        clearIndicator: {color: 'default'},
                        popupIndicator: {color: 'default'},
                    }}
                    renderInput={(params) => {
                        return (
                            <TextField
                                {...params}
                                size={'small'}
                                {...TextFieldProps}
                                error={!!error}
                                helperText={error}
                                value={keywords}
                                inputProps={{
                                    ...params.inputProps,
                                    value: keywords,
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    ...(InputProps ?? {}),
                                    onBlur: (e) => {
                                        setLoadingOptions(false);
                                        params?.onBlur?.(e);
                                        InputProps?.onBlur?.(e);
                                    },
                                    startAdornment: startAdornment,
                                    endAdornment: (
                                        <>
                                            {params.InputProps.endAdornment}
                                            {
                                                (loading || loadingOptions) &&
                                                <CircularProgress size={20} color={'primary'} className={'m-1'}/>
                                            }
                                            {endAdornment}
                                            {searchIcon !== false && (
                                                <IconButton color={'primary'} className={'p-0'} onClick={onSearchClicked}>
                                                    {
                                                        searchIcon ??
                                                        <KeyboardReturnRounded tabIndex={-1}/>
                                                    }
                                                </IconButton>
                                            )}

                                        </>
                                    ),
                                }}
                            />
                        )
                    }}
                    {...AutocompleteProps}
                />
                {
                    showPartQuickLinks && !!partQuickLinks.length &&
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1}} aria-label={t('partQuickLinks')}>
                        <Typography variant={'caption'} fontWeight={600} color={'text.disabled'} sx={{mr: 1}}>
                            {t('partQuickLinks')}:
                        </Typography>
                        {
                            partQuickLinks.map((part) => (
                                <Chip
                                    key={part.id ?? part.partNo}
                                    size="small"
                                    variant="outlined"
                                    color={'primary'}
                                    label={part.partNo}
                                    clickable={!!operatorAccessLevels.packages.lookup.productInformation}
                                    onClick={() => onPartQuickLinkClicked(part)}
                                    aria-label={t('openPart', {partNo: part.partNo})}
                                    icon={<OpenInNewOutlined/>}
                                />
                            ))
                        }
                    </Box>
                }
            </Box>
        </>
    );
}

export default LookupSearchViewSearchAutocomplete;
